import { useEffect, useRef } from "react";
import { mountPitchVideoPlayer } from "../lib/mountPitchVideoPlayer";

let cssTextPromise = null;

function loadPlayerCss() {
  if (!cssTextPromise) {
    const base = process.env.PUBLIC_URL || "";
    cssTextPromise = fetch(`${base}/test-video-player.css`)
      .then((response) => {
        if (!response.ok) throw new Error("Failed to load pitch video player styles");
        return response.text();
      })
      .then(
        (text) => `${text}\n`
      );
  }
  return cssTextPromise;
}

/**
 * Pitch video player for the app — same markup, JS, and CSS as public/test-video.html.
 *
 * Styles are loaded from public/test-video-player.css into a shadow root (never edit player
 * chrome in app stylesheets). To change the player look, edit test-video-player.css only.
 */
export default function PitchVideoPlayer({
  src,
  className = "",
  ariaLabel = "Pitch video",
  preload = "metadata",
  knownDurationSec = 0,
  autoplay = false,
}) {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    const videoSrc = String(src || "").trim();
    if (!host || !videoSrc) return undefined;

    let cancelled = false;
    let unmount = () => {};

    loadPlayerCss()
      .then((cssText) => {
        if (cancelled) return;

        const shadow = host.shadowRoot || host.attachShadow({ mode: "open" });
        shadow.innerHTML = "";

        const style = document.createElement("style");
        style.textContent = cssText;
        shadow.appendChild(style);

        const root = document.createElement("div");
        root.className = "pitch-video-player";
        shadow.appendChild(root);

        unmount = mountPitchVideoPlayer(root, {
          src: videoSrc,
          ariaLabel,
          preload,
          knownDurationSec,
          autoplay,
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      unmount();
    };
  }, [src, ariaLabel, preload, knownDurationSec, autoplay]);

  if (!String(src || "").trim()) return null;

  return (
    <div
      ref={hostRef}
      className={`pitch-video-player-host${className ? ` ${className}` : ""}`}
    />
  );
}
